import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Internal components
import FormError from '../FormError/FormError'

// Utils
import getClosest from '../../utils/dom/getClosest'
import { UserContext } from '../../context/UserContext'

const emptyClass = ' not-empty'
const revealClass = ' is-revealed'
const activeClass = ' is-active'
const selectedClass = 'is-selected'

const acSuggestionsClass = 'FormItem-autocompleteSuggestions'
const acSuggestionClass = 'FormItem-autocompleteSuggestion'

const acSuggestionValueAttr = 'data-val'
const acSuggestionNameAttr = 'data-name'
const acInputSelectedAttr = 'data-selected'

const requiredHint = ' *'
const minChars = 1
const inputDelay = 300

const FormItemAutocomplete = (props) => {
  const { item, groupedState, revealState } = props
  const { register, errors, trigger, getValues, setValue, formState } = useFormContext()

  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // State
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')

  const [clubSearchResults, setClubSearchResults] = useState([])
  const [teamSearchResults, setTeamSearchResults] = useState([])

  const [clubNameValue, setClubNameValue] = useState('')
  const [clubIdValue, setClubIdValue] = useState('')
  const [teamIdValue, setTeamIdValue] = useState('')
  const [teamNameShortValue, setTeamNameShortValue] = useState('')

  // refs
  const itemRef = useRef()
  const inputObj = {}

  const itemInputReg = register(item.name, { required: revealState ? requiredErrorMessage : false })
  const itemInputRef = useRef(null)

  const clubIdInputReg = register(item.clubId.name)
  const clubIdInputRef = useRef(null)

  const teamIdInputReg = register(item.teamSelect.name, { required: revealState ? requiredErrorMessage : false })
  const teamIdInputRef = useRef(null)

  const teamNameInputReg = register(item.teamName.name)
  const teamNameInputRef = useRef(null)

  const teamAgeInputReg = register(item.teamAgeId.name)
  const teamAgeInputRef = useRef(null)

  const teamNameShortInputReg = register(item.teamNameShort.name, {
    required: revealState ? requiredErrorMessage : false
  })
  const teamNameShortInputRef = useRef(null)

  const suggestionRef = useRef()
  const suggestionsObj = {}

  const getClubApiResults = async (term) => {
    if (term.length <= 0) return false
    const clubApiResponse = await fetch(`/api/knvb/clubs?term=${encodeURIComponent(term)}`)
    const clubApiResults = await clubApiResponse.json()
    setClubSearchResults(clubApiResults)
  }

  const getTeamApiResults = async (term) => {
    if (term.length <= 0) return false
    const teamApiResponse = await fetch(`/api/knvb/teams?term=${encodeURIComponent(term)}`)
    const teamApiResults = await teamApiResponse.json()
    setTeamSearchResults(teamApiResults)
  }

  const parseSuggestions = (matches) => {
    const val = getValues(item.name)
    if (matches.length && val.length >= minChars) {
      let s = ''
      matches.forEach((item) => {
        s += renderItem(item, val)
      })
      suggestionRef.current.innerHTML = s
      renderSuggestions()
    } else if (isLoaded) {
      hideSuggestions(suggestionRef)
      setValue(item.clubId.name, '')
      setValue(item.teamNameShort.name, '')
      setClubNameValue('')
      setClubIdValue('')
      setTeamIdValue('')
      setTeamNameShortValue('')
      setTeamSearchResults([])
    }
  }

  const renderSuggestions = () => {
    updateSuggestionsLayout(0)
    suggestionRef.current.style.display = 'block'

    const suggestionList = suggestionRef.current.querySelectorAll(`.${acSuggestionClass}`)
    ;[...suggestionList].forEach((suggestionEl) => {
      suggestionEl.addEventListener('mouseover', (e) => suggestionMouseoverHandler(e))
      suggestionEl.addEventListener('mouseleave', (e) => suggestionMouseleaveHandler(e))
      suggestionEl.addEventListener('mousedown', (e) => suggestionMousedownHandler(e))
    })
  }

  const hideSuggestions = (ref) => {
    if (ref.current) ref.current.style.display = 'none'
  }

  const renderItem = (item, search) => {
    if (!search) return false
    // escape special characters
    search = search.replace(/[-/\\^$*+?.()|[\]{}]/gi, '\\$&')
    const re = new RegExp('(' + search.split(' ').join('|') + ')', 'gi')
    return formatResult(item, re)
  }

  const resetInputValue = () => {
    setValue(item.name, '')
    setValue(item.clubId.name, '')
    setValue(item.teamNameShort.name, '')
    setClubNameValue('')
    setClubIdValue('')
    setTeamIdValue('')
    setTeamNameShortValue('')
    setClubSearchResults([])
    setTeamSearchResults([])
  }

  const formatResult = (item, re) => {
    return `<div class="${acSuggestionClass}" data-val="${item.clubid}" data-name="${item.name}" data-city="${
      item.city
    }"><div class="${acSuggestionClass}Name">${item.name.replace(
      re,
      '<b>$1</b>'
    )}</div><div class="${acSuggestionClass}City">${item.city}</div></div>`
  }

  const inputChangeHandler = (e) => {
    if (e.target.value !== searchTerm) setSearchTerm(e.target.value)
  }

  const inputFocusHandler = (e) => {
    inputObj.lastVal = '\n'
    inputObj.lastId = ''
    itemInputRef.current.removeAttribute(acInputSelectedAttr)
    inputOnKeyUpHandler(e)
  }

  const inputBlurHandler = () => {
    if (!document.querySelector(`[data-input-name=${item.name}].${acSuggestionClass}:hover`)) {
      inputObj.lastVal = getValues(item.name)
      setTimeout(() => hideSuggestions(suggestionRef), 350)
    } else if (itemInputRef.current !== document.activeElement) {
      setTimeout(() => itemInputRef.current.focus(), 20)
    }
    if (!itemInputRef.current.getAttribute(acInputSelectedAttr)) resetInputValue()
  }

  const inputOnKeyUpHandler = (e) => {
    const key = window.event ? e.keyCode : e.which
    if ((!key || key < 35 || key > 40) && key !== 14 && key !== 27) {
      const val = getValues(item.name)
      const id = getValues(item.clubId.name)
      if (val.length < minChars) {
        inputObj.lastVal = val
        inputObj.lastId = id
        hideSuggestions(suggestionRef)
      } else {
        if (val !== inputObj.lastVal) {
          inputObj.lastVal = val
          inputObj.lastId = id
        }
      }
    }
  }

  const inputOnKeyDownHandler = (e) => {
    const key = window.event ? e.keyCode : e.which
    let sel
    // down (40), up (38)
    if ((key === 40 || key === 38) && suggestionRef.current.innerHTML) {
      let next
      sel = suggestionRef.current.querySelector(`.${acSuggestionClass}.${selectedClass}`)
      if (!sel) {
        if (key === 40) {
          next = suggestionRef.current.querySelector(`.${acSuggestionClass}`)
        } else {
          next = suggestionRef.current.childNodes[suggestionRef.current.childNodes.length - 1]
        }
        next.className += ` ${selectedClass}`
        setValue(item.name, next.getAttribute(acSuggestionNameAttr))
      } else {
        next = key === 40 ? sel.nextSibling : sel.previousSibling
        if (next) {
          sel.className = sel.className.replace(selectedClass, '')
          next.className += ` ${selectedClass}`
          setValue(item.name, next.getAttribute(acSuggestionNameAttr))
        } else {
          sel.className = sel.className.replace(selectedClass, '')
          setValue(item.name, inputObj.lastVal)
          next = 0
        }
      }
      updateSuggestionsLayout(0, next)
      return false
    } else if (key === 27) {
      setValue(item.name, inputObj.lastVal)
      hideSuggestions(suggestionRef)
    } else if (key === 13 || key === 9) {
      sel = suggestionRef.current.querySelector(`.${acSuggestionClass}.${selectedClass}`)
      if (sel && suggestionRef.current.style.display !== 'none') {
        suggestionSelectHandler(e, sel.getAttribute(acSuggestionNameAttr), sel.getAttribute(acSuggestionValueAttr), sel)
        if (key === 13) {
          itemInputRef.current.blur()
          e.preventDefault()
        }
        setTimeout(() => hideSuggestions(suggestionRef), 20)
      }
    }
  }

  const suggestionMouseoverHandler = (e) => {
    const sel = suggestionRef.current.querySelector(`.${acSuggestionClass}.${selectedClass}`)
    if (sel) sel.className = sel.className.replace(selectedClass, '')

    let el = e.target
    if (!el.classList.contains(acSuggestionClass)) el = getClosest(e.target, `.${acSuggestionClass}`)
    el.className += ` ${selectedClass}`
  }

  const suggestionMouseleaveHandler = () => {
    const sel = suggestionRef.current.querySelector(`.${acSuggestionClass}.${selectedClass}`)
    if (sel) {
      setTimeout(() => {
        sel.className = sel.className.replace(selectedClass, '')
      }, 20)
    }
  }

  const suggestionMousedownHandler = (e) => {
    let el = e.target
    if (!el.classList.contains(acSuggestionClass)) el = getClosest(e.target, `.${acSuggestionClass}`)
    const v = el.getAttribute(acSuggestionNameAttr)
    const id = el.getAttribute(acSuggestionValueAttr)

    suggestionSelectHandler(e, v, id, el)
    hideSuggestions(suggestionRef)
    e.preventDefault()
  }

  const suggestionSelectHandler = (e, v, id, el) => {
    itemInputRef.current.setAttribute(acInputSelectedAttr, 'true')
    clearSuggestionItems()
    itemInputRef.current.blur()
    setClubNameValue(v)
    setValue(item.name, v)
    setClubIdValue(id)
    setValue(item.clubId.name, id)
  }

  const clearSuggestionItems = () => {
    while (suggestionRef.current.firstChild) suggestionRef.current.removeChild(suggestionRef.current.firstChild)
  }

  const updateSuggestionsLayout = (resize, next) => {
    suggestionRef.current.style.display = 'block'

    if (!suggestionRef.current.maxHeight) {
      suggestionRef.current.maxHeight = parseInt(
        (window.getComputedStyle ? getComputedStyle(suggestionRef.current, null) : suggestionRef.current.currentStyle)
          .maxHeight
      ) // eslint-disable-line no-undef
    }

    if (!suggestionsObj.suggestionHeight) {
      const suggestion = suggestionRef.current.querySelector(`.${acSuggestionClass}`)
      if (suggestion) suggestionsObj.suggestionHeight = suggestion.offsetHeight
    }

    if (suggestionsObj.suggestionHeight) {
      if (!next) {
        suggestionRef.current.scrollTop = 0
      } else {
        const scrTop = suggestionRef.current.scrollTop
        const selTop = next.getBoundingClientRect().top - suggestionRef.current.getBoundingClientRect().top
        if (selTop + suggestionsObj.suggestionHeight - suggestionRef.current.maxHeight > 0) {
          suggestionRef.current.scrollTop =
            selTop + suggestionsObj.suggestionHeight + scrTop - suggestionRef.current.maxHeight
        } else if (selTop < 0) {
          suggestionRef.current.scrollTop = selTop + scrTop
        }
      }
    }
  }

  const onTeamChange = (e) => {
    if (e.target.value !== searchTerm) setTeamIdValue(e.target.value)
  }

  const formatTeamName = (label) => {
    const str = label
    const rest = str.substring(0, str.lastIndexOf(' ') + 1)
    const last = str.substring(str.lastIndexOf(' ') + 1, str.length)
    const value = `${rest}${last.toUpperCase()}`
    return rest && last ? value : label
  }

  useEffect(() => {}, [])

  useEffect(() => {
    if (!item.revealed && item.required && isRequired) setIsRequired({ required: requiredErrorMessage })
    suggestionRef.current.setAttribute('data-input-name', item.name)
    inputObj.cache = {}
    inputObj.lastVal = false
    inputObj.lastId = false
    inputObj.timer = 0

    if (item.value && item.value !== '') {
      setValue(item.name, item.value)
      setClubNameValue(item.value)

      setValue(item.teamName.name, item.teamName.value)
      setValue(item.teamAgeId.name, item.teamAgeId.value.toString())

      setValue(item.clubId.name, item.clubId.value)
      setClubIdValue(item.clubId.value)
    } else {
      setIsLoaded(true)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= minChars) getClubApiResults(searchTerm)
    }, inputDelay)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    parseSuggestions(clubSearchResults)
  }, [clubSearchResults])

  useEffect(() => {
    if (clubIdValue) {
      setTeamSearchResults([])
      const timer = setTimeout(() => getTeamApiResults(clubIdValue), 10)
      return () => clearTimeout(timer)
    }
  }, [clubIdValue])

  useEffect(() => {
    let defaultTeamId = ''
    if (teamSearchResults.length && item.teamSelect.value) {
      teamSearchResults.map((option) => {
        defaultTeamId = option.id === item.teamSelect.value ? option.id : defaultTeamId
        return ''
      })
    }
    if (teamIdInputRef?.current) setValue(item.teamSelect.name, '')
    if (teamNameInputRef?.current) setValue(item.teamName.name, '')
    if (teamNameShortInputRef?.current) setValue(item.teamNameShort.name, '')
    setTeamNameShortValue('')
    setTeamIdValue('')

    if (!isLoaded && defaultTeamId.length >= 1) {
      const timer = setTimeout(() => {
        setValue(item.teamSelect.name, defaultTeamId)
        setTeamIdValue(defaultTeamId)
        if (item.teamNameShort.value) {
          setValue(item.teamNameShort.name, item.teamNameShort.value)
          setTeamNameShortValue(item.teamNameShort.value)
        }
        if (item.teamName.value) {
          setValue(item.teamName.name, item.teamName.value)
        }
      }, 10)
      return () => clearTimeout(timer)
    }
  }, [teamSearchResults])

  useEffect(() => {
    let defaultTeamName = ''
    let defaultShortName = ''
    let defaultTeamAgeId = ''
    if (teamIdValue.length >= 1) {
      teamSearchResults.map((option) => {
        defaultTeamName = option.id === teamIdValue ? formatTeamName(option.label) : defaultTeamName
        defaultShortName = option.id === teamIdValue ? option['team-nameshort'] : defaultShortName
        defaultTeamAgeId = option.id === teamIdValue ? option['team-age-id'] : defaultTeamAgeId
        return ''
      })
    }

    if (defaultShortName.length >= 1 && teamIdValue !== getValues(item.teamSelect.name)) {
      setValue(item.teamSelect.name, teamIdValue)
      setValue(item.teamName.name, defaultTeamName)
      setValue(item.teamAgeId.name, defaultTeamAgeId?.toString())
      setValue(item.teamNameShort.name, defaultShortName)
      trigger()
      setIsLoaded(true)
    }
  }, [teamIdValue])

  useEffect(() => {
    if (item.revealed && item.required) setIsRequired({ required: revealState ? requiredErrorMessage : false })
  }, [revealState])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  return (
    <>
      <div
        className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
          clubNameValue?.length && clubNameValue !== '' ? emptyClass : ''
        }${revealState ? revealClass : ''}${!isGrouped ? '' : activeClass}`}
        ref={itemRef}
        data-reveal-target={item.revealed}
      >
        <label className="FormItem-label" htmlFor={item.name}>{`${item.transl ? t(item.transl) : item.label}${
          item.required ? requiredHint : ''
        }`}</label>
        <input
          type="text"
          id={item.name}
          name={item.name}
          {...itemInputReg}
          ref={(e) => {
            itemInputReg.ref(e)
            itemInputRef.current = e
          }}
          autoComplete="off"
          placeholder={`${item.transl ? t(item.transl) : item.label}${item.required ? requiredHint : ''}`}
          className={`FormItem-input FormItem-input--${item.type}`}
          onInput={(e) => inputChangeHandler(e)}
          onBlur={(e) => inputBlurHandler(e)}
          onFocus={(e) => inputFocusHandler(e)}
          onKeyUp={(e) => inputOnKeyUpHandler(e)}
          onKeyDown={(e) => inputOnKeyDownHandler(e)}
        />
        <div className={acSuggestionsClass} ref={suggestionRef}></div>
        {Object.keys(formState?.errors).length !== 0 && !formState.isValid && formState.isSubmitted && (
          <ErrorMessage errors={errors} name={item.name} as={<FormError />} />
        )}
      </div>
      {item.clubId && (
        <input
          type="hidden"
          id={item.clubId.name}
          name={item.clubId.name}
          {...clubIdInputReg}
          ref={(e) => {
            clubIdInputReg.ref(e)
            clubIdInputRef.current = e
          }}
        />
      )}
      {item.teamSelect && teamSearchResults && (
        <div
          className={`FormItem FormItem--${item.teamSelect.type}${
            item.teamSelect.modifier ? ` FormItem--${item.teamSelect.modifier}` : ''
          }${teamIdValue.length && teamIdValue !== '' ? emptyClass : ''}${revealState ? revealClass : ''}${
            !isGrouped ? '' : activeClass
          }`}
          data-reveal-target={item.revealed}
        >
          <label className="FormItem-label" htmlFor={item.teamSelect.name}>
            {item.teamSelect.transl ? t(item.teamSelect.transl) : item.teamSelect.label}
            {item.teamSelect.required ? requiredHint : ''}
          </label>
          <select
            id={item.teamSelect.name}
            name={item.teamSelect.name}
            {...teamIdInputReg}
            ref={(e) => {
              teamIdInputReg.ref(e)
              teamIdInputRef.current = e
            }}
            defaultValue={item.teamSelect.value ? item.teamSelect.id : ''}
            disabled={!teamSearchResults.length ? 'disabled' : ''}
            className={`FormItem-input FormItem-input--${item.teamSelect.type}`}
            onChange={(e) => onTeamChange(e)}
          >
            {item.teamSelect.default.length &&
              item.teamSelect.default.map((option) => {
                return (
                  <option disabled={option.disabled} value={''} key="FormItem-option--0">
                    {option.transl ? t(option.transl) : option.label}
                  </option>
                )
              })}
            {teamSearchResults.length >= 1 &&
              teamSearchResults.map((option, index) => {
                return (
                  <option value={option.id} key={`FormItem-option--${index + 1}`}>
                    {formatTeamName(option.label)}
                  </option>
                )
              })}
          </select>
          {Object.keys(formState?.errors).length !== 0 && !formState.isValid && formState.isSubmitted && (
            <ErrorMessage errors={errors} name={item.teamSelect.name} as={<FormError />} />
          )}
        </div>
      )}
      {item.teamName && (
        <input
          type="hidden"
          id={item.teamName.name}
          name={item.teamName.name}
          {...teamNameInputReg}
          ref={(e) => {
            teamNameInputReg.ref(e)
            teamNameInputRef.current = e
          }}
        />
      )}
      {item.teamAgeId && (
        <input
          type="hidden"
          id={item.teamAgeId.name}
          name={item.teamAgeId.name}
          {...teamAgeInputReg}
          ref={(e) => {
            teamAgeInputReg.ref(e)
            teamAgeInputRef.current = e
          }}
        />
      )}
      {item.teamNameShort && (
        <div
          className={`FormItem FormItem--${item.teamNameShort.type}${
            item.teamNameShort.modifier ? ` FormItem--${item.teamNameShort.modifier}` : ''
          }${teamNameShortValue.length && teamNameShortValue !== '' ? emptyClass : ''}${
            revealState ? revealClass : ''
          }${!isGrouped ? '' : activeClass}`}
          data-reveal-target={item.revealed}
        >
          <label className="FormItem-label" htmlFor={item.teamNameShort.name}>{`${
            item.teamNameShort.transl ? t(item.teamNameShort.transl) : item.teamNameShort.label
          }${item.teamNameShort.required ? requiredHint : ''}`}</label>
          <input
            type="text"
            id={item.teamNameShort.name}
            name={item.teamNameShort.name}
            {...teamNameShortInputReg}
            ref={(e) => {
              teamNameShortInputReg.ref(e)
              teamNameShortInputRef.current = e
            }}
            defaultValue={item.teamNameShort.value}
            disabled={!teamSearchResults.length ? 'disabled' : ''}
            placeholder={`${item.teamNameShort.transl ? t(item.teamNameShort.transl) : item.teamNameShort.label}${
              item.teamNameShort.required ? requiredHint : ''
            }`}
            maxLength={item.teamNameShort.maxLength}
            className={`FormItem-input FormItem-input--${item.teamNameShort.type}`}
          />
          {Object.keys(formState?.errors).length !== 0 && !formState.isValid && formState.isSubmitted && (
            <ErrorMessage errors={errors} name={item.teamNameShort.name} as={<FormError />} />
          )}
        </div>
      )}
    </>
  )
}

FormItemAutocomplete.propTypes = {
  item: PropTypes.object,
  onFormItemLoad: PropTypes.func,
  onFormItemChange: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any
}

export default FormItemAutocomplete
